import React, { useState, useContext } from "react"

// Context
import { UserContext } from "../../../../context/userContext"

//Apollo
import _, { get } from "lodash"
import { useLazyQuery } from "@apollo/client"
import { GET_USERS, GET_USER } from "../queries"

// Styles
import refEntityStyles from "../../../../styles/referrals/ReferralEntityForm.module.css"

// Prime React
import { FilterMatchMode } from "primereact/api"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { InputText } from "primereact/inputtext"
import { Skeleton } from "primereact/skeleton"
import { Button } from "primereact/button"

//Referrals
import { mapUserRoleToFriendlyName } from "../sharedFunctions"
import { LogError } from "../../../../helpers/logger"
import ReferralEntityUserDialog from "./ReferralEntityUserDialog"
import { useEffect } from "react"

export default function ReferralEntityUserList({
  isDialog,
  partnerOrganizationId,
  readOnly,
  partnerIsUnconfirmed,
  partnerCreationOrganization,
}) {
  const userCtx = useContext(UserContext)

  const userQueryVariables = {
    variables: {
      uid: "-7",
      organization_id:
        partnerOrganizationId || userCtx.session.user.organization_id,
    },
  }
  const partnerAdminRoleId = 9
  const appAdminRoleId = 1

  const [isLoading, setIsLoading] = useState(true)
  const [refEntityUserData, setRefEntityUserData] = useState()
  const [globalFilterValue, setGlobalFilterValue] = useState("")
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  })
  const [userProfile, setUserProfile] = useState(undefined)
  const [showUserForm, setShowUserForm] = useState(false)

  const onGlobalFilterChange = (e) => {
    const value = e.target.value
    let _filters = { ...filters }
    _filters["global"].value = value

    setFilters(_filters)
    setGlobalFilterValue(value)
  }

  const renderHeader = () => {
    const allowRolesToAddUsers = [appAdminRoleId, partnerAdminRoleId]

    return (
      <div style={{ display: "inline-block", width: "100%" }}>
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Keyword Search"
            disabled={isLoading}
          />
        </span>

        {allowRolesToAddUsers.includes(userCtx.session.user.staff_role) &&
        !readOnly ? (
          <div style={{ float: "right" }}>
            <Button
              type="button"
              className={refEntityStyles.button}
              onClick={() => {
                setUserProfile(undefined)
                setShowUserForm(true)
              }}
              disabled={isLoading}
            >
              New User
            </Button>
          </div>
        ) : null}
      </div>
    )
  }

  const actionBodyTemplate = (rowData) => {
    if (!!readOnly) return null

    let allowEdit = [appAdminRoleId, partnerAdminRoleId].includes(
      userCtx.session.user.staff_role
    )

    //If this is an Enterprise or Organization admin user and the organization is the creator of the partner, allow editing of user if:
    //1. partner profile not confirmed
    //2. Partner administrator has not logged in before
    if (
      !allowEdit &&
      partnerIsUnconfirmed &&
      refEntityUserData?.length === 1 &&
      [2, 3].includes(userCtx.session.user.staff_role) &&
      userCtx.session?.organizations?.some(
        (x) => x.organization_id === partnerCreationOrganization
      )
    ) {
      allowEdit =
        !rowData.staff_last_login ||
        rowData.staff_last_login === "No session found."
    }

    if (!allowEdit) {
      return null
    }

    return (
      <div style={{ width: "100%" }}>
        <Button
          type="button"
          onClick={() => {
            setUserProfile(rowData)
            setShowUserForm(true)
          }}
          className={refEntityStyles.button}
          style={{ float: "right" }}
          disabled={isLoading}
        >
          Edit
        </Button>
      </div>
    )
  }

  const [getUsers] = useLazyQuery(GET_USERS, {
    variables: userQueryVariables.variables,
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (!!data.users) {
        setRefEntityUserData(
          _.map(data.users, (user) => {
            return {
              ...user,
              staff_role_name: mapUserRoleToFriendlyName(
                user.staff_role,
                user.staff_role_name
              ),
              full_name: `${_.unescape(user.staff_name_first)} ${_.unescape(
                user.staff_name_last
              )}`,
            }
          })
        )
      } else {
        setRefEntityUserData([])
      }
      setIsLoading(false)
    },
    onError: (error) => {
      userCtx.setNotificationError(
        `Unable to load users, see console for more detail`
      )
      LogError("Unable to retrieve user list", error)
      setIsLoading(false)
      setRefEntityUserData([])
    },
  })

  useEffect(() => {
    getUsers({ ...userQueryVariables })
  }, [getUsers])

  return (
    <div>
      {!isDialog ? <h1>Partner Staff Accounts</h1> : null}

      <ReferralEntityUserDialog
        partnerOrganizationId={partnerOrganizationId}
        userProfile={userProfile}
        showDialog={showUserForm}
        onHideDialog={() => {
          setUserProfile(undefined)
          setShowUserForm(false)
        }}
        onChanged={() => {
          setIsLoading(true)
          setUserProfile(undefined)
          setShowUserForm(false)
          getUsers({ ...userQueryVariables })
        }}
      />
      <DataTable
        header={renderHeader}
        filters={filters}
        globalFilterFields={[
          "full_name",
          "referral_entity_city",
          "referral_entity_status",
          "date_created",
        ]}
        value={isLoading ? Array.from({ length: 5 }) : refEntityUserData}
        paginator
        rows={6}
      >
        <Column
          field="full_name"
          sortable
          header="Name"
          body={isLoading ? <Skeleton /> : null}
          style={{ width: "40%" }}
        />
        <Column
          field="organization_name"
          sortable
          header="Organization"
          body={isLoading ? <Skeleton /> : null}
        />
        <Column
          field="staff_user_status"
          sortable
          header="Status"
          body={isLoading ? <Skeleton /> : null}
        />
        <Column
          field="staff_role_name"
          sortable
          header="Role"
          body={isLoading ? <Skeleton /> : null}
        />
        {isLoading ? null : (
          <Column
            header=""
            body={actionBodyTemplate}
            alignFrozen="right"
            frozen
            style={{ width: "100px" }}
            resizeable={false}
          />
        )}
      </DataTable>
    </div>
  )
}
